import "amazon-connect-streams";
import { getLogger } from "@expert/logging";

const logger = getLogger({ module: "createChatJSConnection" });

/**
 * Creates a new ChatJS connection (`connect.AgentChatSession`) for a given contact.
 *
 * This function handles the low-level connection logic and returns the live session object,
 * which should then be stored in our central connection registry/cache.
 * @throws If agent connection details are missing or the session fails to connect.
 */
export function createChatJSConnection(contact: connect.Contact, region: string): connect.AgentChatSession {
    const contactLogger = logger.child({ contactId: contact.getContactId() });

    contactLogger.debug("Attempting to create a new ChatJS agent connection object.");

    const agentConnection = contact.getAgentConnection() as connect.ChatConnection;
    if (!agentConnection) {
        throw new Error("No agent connection available for chat contact.");
    }

    const chatDetails = agentConnection.getMediaInfo();
    if (!chatDetails) {
        throw new Error("No chat details available from agent connection.");
    }

    // ! CRITICAL: Per-session options override global config
    // ! always pass region in options to ensure consistency between global config
    // ChatJS Library defaults to (region: "us-west-2") when no region is passed in options
    const newConnection = connect.ChatSession.create({
        chatDetails,
        type: connect.ChatSession.SessionTypes.AGENT,
        options: { region },
        websocketManager: connect.core.getWebSocketManager(),
    });

    if (!newConnection) {
        throw new Error("connect.ChatSession.create failed to return a session.");
    }

    contactLogger.info("New ChatJS connection object created, not yet connected.");

    return newConnection;
}
